import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild
} from '@angular/core';

@Component({
    selector: 'app-power-switch',
    templateUrl: './power-switch.component.html',
    styleUrls: ['./power-switch.component.scss']
})
export class PowerSwitchComponent implements OnInit, OnChanges {

    @Input() state = false;
    @Input() style: PowerSwitchStyle = null;
    @Output() stateChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('knob', {static: true}) private knob: ElementRef;
    @ViewChild('knobContainer', {static: true}) private knobContainer: ElementRef;
    @ViewChild('indicator', {static: true}) private indicator: ElementRef;

    private mouseDown = false;
    private touchDown = false;
    private elementSize: number = null;
    private elementOrigin: number = null;
    private containerWidth: number = null;
    private containerStart: number = null;
    private containerEnd: number = null;

    private mouseDownStart: number = null;
    private touchDownStart: number = null;

    constructor(private renderer: Renderer2) {
    }

    ngOnInit() {
        this.initialize();
        this.initializeMouseHandlers();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.renderer.removeStyle(this.knob.nativeElement, 'right');
        if (this.state) {
            this.setKnobStyle('left', (this.containerWidth - this.elementSize));
        } else {
            this.setKnobStyle('left', 0);
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        this.initialize();
    }

    /**
     * Initialize
     */
    private initialize(): void {
        if (this.style !== null) {
            this.setKnobContainerStyle('width', this.style.containerWidth);
            this.setKnobContainerStyle('height', this.style.elementSize);

            this.setKnobStyle('width', this.style.elementSize);
            this.setKnobStyle('height', this.style.elementSize);
            this.setKnobStyle('border-radius', (this.style.elementSize / 2));

            if (this.style.darkTheme) {
                this.renderer.addClass(this.knob.nativeElement, 'dark');
                this.renderer.addClass(this.indicator.nativeElement, 'dark');
            } else {
                this.renderer.addClass(this.knob.nativeElement, 'light');
                this.renderer.addClass(this.indicator.nativeElement, 'light');
            }

        }

        this.elementOrigin = this.knob.nativeElement.getBoundingClientRect().x;
        this.elementSize = this.knob.nativeElement.offsetWidth;

        this.containerWidth = this.knobContainer.nativeElement.offsetWidth;
        this.containerStart = this.elementOrigin + (this.elementSize / 2);
        this.containerEnd = this.elementOrigin + this.containerWidth - (this.elementSize / 2);

        this.setIndicatorStyle('height', (0.18 * this.elementSize));
        this.renderer.setStyle(this.indicator.nativeElement,
            'top', `calc(50% - ( ${0.18 * this.elementSize}px / 2))`);
        this.renderer.setStyle(this.indicator.nativeElement,
            'border-radius', `${(0.18 * this.elementSize) / 2}px`);

        this.renderer.removeStyle(this.knob.nativeElement, 'right');
        if (this.state) {
            this.setKnobStyle('left', this.containerWidth - this.elementSize);
        } else {
            this.setKnobStyle('left', 0);
        }
    }

    /**
     * Initialize Mouse callbacks
     */
    private initializeMouseHandlers(): void {
        /* mousedown */
        this.renderer.listen(this.knob.nativeElement, 'mousedown',
            (event) => {
                this.mouseDown = true;
                this.mouseDownStart = Date.now();
            }
        );

        this.renderer.listen(this.knob.nativeElement, 'touchstart',
            (event) => {
                this.touchDown = true;
                this.touchDownStart = Date.now();
            }
        );

        /* mousemove */
        this.renderer.listen(this.knob.nativeElement, 'mousemove',
            (event) => {
                if (this.mouseDown) {
                    const new_x = this.calcNewPosition(event.x, event.y);
                    this.renderer.removeStyle(this.knob.nativeElement, 'right');
                    this.setKnobStyle('left', new_x);
                    if (new_x < (this.containerWidth - this.elementSize) / 2) {
                        if (this.state) {
                            this.state = false;
                        }
                    } else {
                        if (!this.state) {
                            this.state = true;
                        }
                    }
                }
            }
        );

        this.renderer.listen(this.knob.nativeElement, 'touchmove',
            (event) => {
                if (this.touchDown) {
                    const new_x = this.calcNewPosition(event.changedTouches[0].clientX, event.changedTouches[0].clientY);
                    this.renderer.removeStyle(this.knob.nativeElement, 'right');
                    this.setKnobStyle('left', new_x);
                    if (new_x < (this.containerWidth - this.elementSize) / 2) {
                        if (this.state) {
                            this.state = false;
                        }
                    } else {
                        if (!this.state) {
                            this.state = true;
                        }
                    }
                }
            }
        );


        /* mouseup */
        this.renderer.listen(this.knob.nativeElement, 'mouseup',
            (event) => {
                const mouse_down_end = Date.now();

                // click
                if (mouse_down_end - this.mouseDownStart < 200) {
                    this.state = !this.state;
                    this.renderer.removeStyle(this.knob.nativeElement, 'left');
                    if (this.state) {
                        this.setKnobStyle('right', 0);
                    } else {
                        this.setKnobStyle('right', (this.containerWidth - this.elementSize));
                    }
                    this.mouseDown = false;
                    this.stateChange.emit(this.state);
                    return;
                }

                // drag
                const new_x = this.calcNewPosition(event.x, event.y);
                this.renderer.removeStyle(this.knob.nativeElement, 'right');
                if (new_x < (this.containerWidth - this.elementSize) / 2) {
                    this.setKnobStyle('left', 0);
                    this.state = false;
                } else {
                    this.setKnobStyle('left', (this.containerWidth - this.elementSize));
                    this.state = true;
                }
                this.stateChange.emit(this.state);

                this.mouseDown = false;
            }
        );

        this.renderer.listen(this.knob.nativeElement, 'touchend',
            (event) => {
                if (this.touchDown) {
                    const touch_down_end = Date.now();

                    // click
                    if (touch_down_end - this.touchDownStart < 200) {
                        this.state = !this.state;
                        this.renderer.removeStyle(this.knob.nativeElement, 'left');
                        if (this.state) {
                            this.setKnobStyle('right', 0);
                        } else {
                            this.setKnobStyle('right', (this.containerWidth - this.elementSize));
                        }
                        this.touchDown = false;
                        this.stateChange.emit(this.state);
                        return;
                    }

                    // drag
                    const new_x = this.calcNewPosition(event.changedTouches[0].clientX, event.changedTouches[0].clientY);
                    this.renderer.removeStyle(this.knob.nativeElement, 'right');
                    if (new_x < (this.containerWidth - this.elementSize) / 2) {
                        this.setKnobStyle('left', 0);
                        this.state = false;
                    } else {
                        this.setKnobStyle('left', (this.containerWidth - this.elementSize));
                        this.state = true;
                    }
                    this.stateChange.emit(this.state);

                    this.touchDown = false;
                }
            }
        );

        /* mouseout */
        this.renderer.listen(this.knob.nativeElement, 'mouseout',
            (event) => {
                if (this.mouseDown) {
                    const new_x = this.calcNewPosition(event.x, event.y);
                    if (new_x < (this.containerWidth - this.elementSize) / 2) {
                        this.setKnobStyle('left', 0);
                        this.state = false;
                    } else {
                        this.setKnobStyle('left', (this.containerWidth - this.elementSize));
                        this.state = true;
                    }
                    this.stateChange.emit(this.state);
                    this.mouseDown = false;
                }
            }
        );

        this.renderer.listen(this.knob.nativeElement, 'touchcancel',
            (event) => {
                if (this.touchDown) {
                    const new_x = this.calcNewPosition(event.changedTouches[0].clientX, event.changedTouches[0].clientY);
                    if (new_x < (this.containerWidth - this.elementSize) / 2) {
                        this.setKnobStyle('left', 0);
                        this.state = false;
                    } else {
                        this.setKnobStyle('left', (this.containerWidth - this.elementSize));
                        this.state = true;
                    }
                    this.stateChange.emit(this.state);
                    this.touchDown = false;
                }
            }
        );

    }


    /**
     * Calculates the position after the element was dragged.
     * @param x
     * @param y
     */
    private calcNewPosition(x: number, y: number): number {
        const current_local_x = x - this.elementOrigin;
        if (x < this.containerStart) {
            return 0;
        }
        if (x > this.containerEnd) {
            return this.containerWidth - this.elementSize;
        }
        return current_local_x - (this.elementSize / 2);
    }

    private setIndicatorStyle(attribute: string, value: number | string): void {
        this.renderer.setStyle(
            this.indicator.nativeElement,
            attribute,
            `${value}px`
        );
    }

    private setKnobStyle(attribute: string, value: number): void {
        this.renderer.setStyle(
            this.knob.nativeElement,
            attribute,
            `${value}px`
        );
    }

    private setKnobContainerStyle(attribute: string, value: number): void {
        this.renderer.setStyle(
            this.knob.nativeElement,
            attribute,
            `${value}px`
        );
    }

}

export interface PowerSwitchStyle {
    containerWidth: number;
    elementSize: number;
    darkTheme: boolean;
}
